import clsx from 'clsx'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

// The shared look of a field (design system "Поля · одна висота 44"): inputs,
// selects and text areas are 44px tall with a 1px border at 18% ink, radius 12
// and 15px text; an invalid field gets a 2px danger border. Phones get 16px:
// iOS Safari zooms the page into any smaller field on focus.
export const FIELD =
  'w-full min-h-11 h-11 px-3.5 py-1 rounded-xl bg-surface border border-solid border-ink/[0.18] text-ink placeholder:text-ink-3 text-base md:text-[0.9375rem] font-normal transition-colors duration-200 hover:border-ink/30 focus:outline-none focus:border-brand focus:shadow-[0_0_0_0.1875rem_rgba(83,112,246,0.2)]'
export const FIELD_INVALID = '!border-2 !border-danger !px-[0.8125rem]'

interface IFieldLabel {
  label: string
  required?: boolean
  optional?: boolean
  className?: string
  // the id of the field: the label then belongs to it — a screen reader
  // reads it as the field's name and a tap on it puts the caret there
  htmlFor?: string
}

// 14px bold label; a required field gets a danger asterisk, an optional one a
// muted "(необов'язково)" instead of the same bold text
export const FieldLabel: FC<IFieldLabel> = ({ label, required, optional, className, htmlFor }) => {
  const { t } = useTranslation()
  const classes = clsx('text-sm font-bold leading-5 text-ink', className)
  const content = (
    <>
      {label}
      {required && <span className="ml-0.5 text-danger">*</span>}
      {optional && <span className="font-normal text-ink-2"> ({t('optional_label')})</span>}
    </>
  )
  return htmlFor ? (
    <label htmlFor={htmlFor} className={classes}>
      {content}
    </label>
  ) : (
    <span className={classes}>{content}</span>
  )
}

// The message under an invalid field, in the flow: the old one hung under the
// field in 11px and overlapped whatever came next. Its id lets the field name
// it with aria-describedby, so a screen reader reads it with the field.
const FieldError: FC<{ message: string; id?: string }> = ({ message, id }) => (
  <span
    id={id}
    className="flex items-start gap-1.5 mt-1.5 text-[0.8125rem] font-bold leading-[1.125rem] text-danger"
  >
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className="shrink-0 mt-px"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6M12 16.5v.5" />
    </svg>
    {message}
  </span>
)

export default FieldError
